import React, { useState, useRef, useEffect } from 'react';

const WEEKDAYS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];
const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const pad = (n) => String(n).padStart(2, '0');
const toISO = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const parseISO = (s) => {
  if (!s) return null;
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d);
};
const isSameDay = (a, b) => (
  !!a && !!b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()
);
const formatDisplay = (iso) => {
  const d = parseISO(iso);
  if (!d) return '';
  return `${d.getDate()} ${MONTH_NAMES[d.getMonth()].slice(0, 3)} ${d.getFullYear()}`;
};

export const getTodayISO = () => toISO(new Date());

/**
 * Small popover calendar, used in place of the native <input type="date">
 * (which just shows an empty dd/mm/yyyy placeholder and needs manual
 * segment typing). Always resets to today's month whenever it's opened, and
 * today's date is visibly ringed in the grid, so "pick now" is always a
 * single click via the Today shortcut regardless of what was last viewed.
 *
 * Props:
 *   - value: 'YYYY-MM-DD' | '' | null
 *   - onChange: (value) => void -- 'YYYY-MM-DD', or null when cleared
 *   - disabled, id
 */
const DatePicker = ({ value, onChange, disabled, id }) => {
  const today = new Date();
  const selected = parseISO(value);
  const [isOpen, setIsOpen] = useState(false);
  const [viewYear, setViewYear] = useState((selected || today).getFullYear());
  const [viewMonth, setViewMonth] = useState((selected || today).getMonth());
  const containerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    const handleEscape = (e) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  const openPicker = () => {
    if (disabled) return;
    const anchor = selected || today;
    setViewYear(anchor.getFullYear());
    setViewMonth(anchor.getMonth());
    setIsOpen((open) => !open);
  };

  const goToToday = () => {
    setViewYear(today.getFullYear());
    setViewMonth(today.getMonth());
    onChange(toISO(today));
    setIsOpen(false);
  };

  const handlePickDay = (day) => {
    onChange(toISO(new Date(viewYear, viewMonth, day)));
    setIsOpen(false);
  };

  const handleClear = () => {
    onChange(null);
    setIsOpen(false);
  };

  const changeMonth = (delta) => {
    let m = viewMonth + delta;
    let y = viewYear;
    if (m < 0) { m = 11; y -= 1; }
    if (m > 11) { m = 0; y += 1; }
    setViewMonth(m);
    setViewYear(y);
  };

  const firstOfMonth = new Date(viewYear, viewMonth, 1);
  const startOffset = (firstOfMonth.getDay() + 6) % 7; // Monday-first grid
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startOffset; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  return (
    <div className="date-picker" ref={containerRef}>
      <button
        type="button"
        id={id}
        className="date-picker-trigger form-input"
        onClick={openPicker}
        disabled={disabled}
      >
        <span>{value ? formatDisplay(value) : 'No due date'}</span>
        <span aria-hidden="true">📅</span>
      </button>

      {isOpen && (
        <div className="date-picker-popover" role="dialog" aria-label="Choose a date">
          <div className="date-picker-header">
            <button type="button" className="date-picker-nav-btn" onClick={() => changeMonth(-1)} aria-label="Previous month">&lsaquo;</button>
            <span className="date-picker-month-label">{MONTH_NAMES[viewMonth]} {viewYear}</span>
            <button type="button" className="date-picker-nav-btn" onClick={() => changeMonth(1)} aria-label="Next month">&rsaquo;</button>
          </div>
          <div className="date-picker-grid">
            {WEEKDAYS.map((w) => <div key={w} className="date-picker-weekday">{w}</div>)}
            {cells.map((day, idx) => {
              if (day === null) return <div key={`blank-${idx}`} />;
              const cellDate = new Date(viewYear, viewMonth, day);
              const classes = ['date-picker-day'];
              if (isSameDay(cellDate, today)) classes.push('today');
              if (selected && isSameDay(cellDate, selected)) classes.push('selected');
              return (
                <button
                  key={day}
                  type="button"
                  className={classes.join(' ')}
                  onClick={() => handlePickDay(day)}
                >
                  {day}
                </button>
              );
            })}
          </div>
          <div className="date-picker-footer">
            <button type="button" onClick={goToToday}>Today</button>
            <button type="button" onClick={handleClear}>Clear</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DatePicker;
